// Top 5 Intelligence — management prioritization layer.
// Prototype-local typed data, shaped so each type can map 1:1 to a future
// database entity (top5_item, warranty_claim).

export type Top5View =
  "problems" | "components" | "products" | "carlines" | "suppliers" | "failure-modes";

export type RankBasis =
  "claims" | "cost" | "rate" | "severity" | "trend" | "customerImpact" | "repeat";

export type Trend = "Rising" | "Stable" | "Falling";
export type RiskLevel = "Critical" | "High" | "Medium" | "Low";

export type Top5Item = {
  id: string;
  view: Top5View;
  name: string;
  description: string;
  claims: number;
  cost: number; // USD
  ratePer1000: number | null; // claims / 1,000 units in service
  severity: RiskLevel;
  severityScore: number; // 1-10 (PFMEA severity aligned)
  trend: Trend;
  trendPct: number; // MoM % change in claims
  repeatOccurrence: boolean;
  repeatCount: number; // prior occurrences of same failure signature
  customerImpact: number; // 0-100 composite (safety, downtime, satisfaction)
  carLine: string;
  component: string;
  supplier: string;
  failureMode: string;
  linkedCaseId?: string;
  problemRecordId?: string;
  problemRecordStatus?: string;
  spcSignal?: string;
};

export const top5ViewMeta: { key: Top5View; label: string; blurb: string }[] = [
  {
    key: "problems",
    label: "Top 5 Problems",
    blurb: "Ranked field problems driving warranty exposure",
  },
  {
    key: "components",
    label: "Top 5 Components",
    blurb: "Part-level cost and claim concentration",
  },
  { key: "products", label: "Top 5 Products", blurb: "Product/program level warranty performance" },
  { key: "carlines", label: "Top 5 Car Lines", blurb: "Vehicle platform exposure and C/1000" },
  { key: "suppliers", label: "Top 5 Suppliers", blurb: "Supplier-attributable warranty burden" },
  {
    key: "failure-modes",
    label: "Top 5 Failure Modes",
    blurb: "Recurring physics-of-failure signatures",
  },
];

export const rankBasisMeta: { key: RankBasis; label: string; unit: string }[] = [
  { key: "claims", label: "Claim Count", unit: "claims" },
  { key: "cost", label: "Warranty Cost", unit: "USD" },
  { key: "rate", label: "Claims / 1,000", unit: "C/1000" },
  { key: "severity", label: "Severity", unit: "sev score" },
  { key: "trend", label: "Trend", unit: "MoM %" },
  { key: "customerImpact", label: "Customer Impact", unit: "index" },
  { key: "repeat", label: "Repeat Occurrence", unit: "recurrences" },
];

export const top5Items: Top5Item[] = [
  // ---------- PROBLEMS ----------
  {
    id: "P-01",
    view: "problems",
    name: "HV battery pack thermal shutdown",
    description:
      "Thermal runaway precursor triggers pack disable; batch B-2204 cells over-represented.",
    claims: 412,
    cost: 3840000,
    ratePer1000: 33.0,
    severity: "Critical",
    severityScore: 9,
    trend: "Rising",
    trendPct: 18.4,
    repeatOccurrence: true,
    repeatCount: 2,
    customerImpact: 96,
    carLine: "EV-X Platform",
    component: "HV Battery Pack",
    supplier: "Nordcell Energy AB",
    failureMode: "Thermal runaway / cell separator breach",
    linkedCaseId: "8D-2041",
    problemRecordId: "PR-2026-014",
    problemRecordStatus: "Active — Validation",
    spcSignal: "3σ breach · 4 consecutive months above UCL",
  },
  {
    id: "P-02",
    view: "problems",
    name: "Inverter IGBT thermal derate",
    description: "Power loss event under sustained load; gate driver thermal margin insufficient.",
    claims: 268,
    cost: 1420000,
    ratePer1000: 21.5,
    severity: "High",
    severityScore: 8,
    trend: "Rising",
    trendPct: 11.2,
    repeatOccurrence: false,
    repeatCount: 0,
    customerImpact: 78,
    carLine: "EV-X Platform",
    component: "Traction Inverter",
    supplier: "Helios Power Systems",
    failureMode: "Thermal derate / power stage overheat",
    problemRecordId: "PR-2026-016",
    problemRecordStatus: "Active — Root Cause",
    spcSignal: "Jan '26 point above UCL",
  },
  {
    id: "P-03",
    view: "problems",
    name: "ABS caliper seal brake drag",
    description: "Seal swell causes residual clamp load, pad wear and reduced range.",
    claims: 231,
    cost: 980000,
    ratePer1000: 17.8,
    severity: "High",
    severityScore: 8,
    trend: "Stable",
    trendPct: 1.4,
    repeatOccurrence: true,
    repeatCount: 1,
    customerImpact: 71,
    carLine: "Hybrid Series",
    component: "Brake System",
    supplier: "FrenoTech S.p.A.",
    failureMode: "Elastomer swell / residual drag",
    problemRecordId: "PR-2026-011",
    problemRecordStatus: "Active — Effectiveness",
  },
  {
    id: "P-04",
    view: "problems",
    name: "Infotainment cold-start boot loop",
    description: "Bootloader 4.x watchdog race on cold soak below -15 °C.",
    claims: 197,
    cost: 410000,
    ratePer1000: 14.2,
    severity: "Medium",
    severityScore: 5,
    trend: "Falling",
    trendPct: -22.6,
    repeatOccurrence: true,
    repeatCount: 3,
    customerImpact: 44,
    carLine: "ICE Legacy",
    component: "Infotainment Head Unit",
    supplier: "Lumen Electronics",
    failureMode: "Firmware watchdog race",
    linkedCaseId: "8D-2036",
    problemRecordId: "PR-2026-008",
    problemRecordStatus: "Closed — Prevention verified",
  },
  {
    id: "P-05",
    view: "problems",
    name: "Drive unit bearing premature wear",
    description: "Out-of-round race from supplier lot variance drives NVH complaints at 25k mi.",
    claims: 154,
    cost: 720000,
    ratePer1000: 9.6,
    severity: "Medium",
    severityScore: 7,
    trend: "Rising",
    trendPct: 7.9,
    repeatOccurrence: false,
    repeatCount: 0,
    customerImpact: 52,
    carLine: "Fleet",
    component: "Drive Unit",
    supplier: "Rotorwerk GmbH",
    failureMode: "Bearing roundness / NVH",
    linkedCaseId: "8D-2031",
  },

  // ---------- COMPONENTS ----------
  {
    id: "C-01",
    view: "components",
    name: "HV Battery Pack",
    description: "High-voltage energy storage assembly — EV-X and fleet applications.",
    claims: 468,
    cost: 4120000,
    ratePer1000: 37.6,
    severity: "Critical",
    severityScore: 9,
    trend: "Rising",
    trendPct: 16.1,
    repeatOccurrence: true,
    repeatCount: 2,
    customerImpact: 95,
    carLine: "EV-X Platform",
    component: "HV Battery Pack",
    supplier: "Nordcell Energy AB",
    failureMode: "Thermal runaway / cell separator breach",
    linkedCaseId: "8D-2041",
    problemRecordId: "PR-2026-014",
    problemRecordStatus: "Active — Validation",
    spcSignal: "3σ breach",
  },
  {
    id: "C-02",
    view: "components",
    name: "Traction Inverter",
    description: "Power electronics stage converting DC pack energy to motor drive.",
    claims: 291,
    cost: 1560000,
    ratePer1000: 23.4,
    severity: "High",
    severityScore: 8,
    trend: "Rising",
    trendPct: 12.7,
    repeatOccurrence: false,
    repeatCount: 0,
    customerImpact: 76,
    carLine: "EV-X Platform",
    component: "Traction Inverter",
    supplier: "Helios Power Systems",
    failureMode: "Thermal derate",
    problemRecordId: "PR-2026-016",
    problemRecordStatus: "Active — Root Cause",
  },
  {
    id: "C-03",
    view: "components",
    name: "Brake System",
    description: "Caliper, seal and actuation hardware across hybrid platforms.",
    claims: 244,
    cost: 1030000,
    ratePer1000: 18.6,
    severity: "High",
    severityScore: 8,
    trend: "Stable",
    trendPct: 0.8,
    repeatOccurrence: true,
    repeatCount: 1,
    customerImpact: 73,
    carLine: "Hybrid Series",
    component: "Brake System",
    supplier: "FrenoTech S.p.A.",
    failureMode: "Residual drag",
    problemRecordId: "PR-2026-011",
    problemRecordStatus: "Active — Effectiveness",
  },
  {
    id: "C-04",
    view: "components",
    name: "Infotainment Head Unit",
    description: "Center-stack compute and display module, bootloader 4.x family.",
    claims: 212,
    cost: 448000,
    ratePer1000: 15.1,
    severity: "Medium",
    severityScore: 5,
    trend: "Falling",
    trendPct: -19.8,
    repeatOccurrence: true,
    repeatCount: 3,
    customerImpact: 42,
    carLine: "ICE Legacy",
    component: "Infotainment Head Unit",
    supplier: "Lumen Electronics",
    failureMode: "Firmware watchdog race",
    linkedCaseId: "8D-2036",
    problemRecordId: "PR-2026-008",
    problemRecordStatus: "Closed — Prevention verified",
  },
  {
    id: "C-05",
    view: "components",
    name: "Drive Unit",
    description: "e-Axle gearbox and bearing stack for fleet duty cycles.",
    claims: 168,
    cost: 790000,
    ratePer1000: 10.4,
    severity: "Medium",
    severityScore: 7,
    trend: "Rising",
    trendPct: 6.4,
    repeatOccurrence: false,
    repeatCount: 0,
    customerImpact: 50,
    carLine: "Fleet",
    component: "Drive Unit",
    supplier: "Rotorwerk GmbH",
    failureMode: "Bearing roundness",
    linkedCaseId: "8D-2031",
  },

  // ---------- PRODUCTS ----------
  {
    id: "PR-01",
    view: "products",
    name: "PowerCell X7",
    description: "Flagship 800V battery product shipped across EV-X and fleet programs.",
    claims: 412,
    cost: 3840000,
    ratePer1000: 33.0,
    severity: "Critical",
    severityScore: 9,
    trend: "Rising",
    trendPct: 18.4,
    repeatOccurrence: true,
    repeatCount: 2,
    customerImpact: 96,
    carLine: "EV-X Platform",
    component: "HV Battery Pack",
    supplier: "Nordcell Energy AB",
    failureMode: "Thermal runaway",
    linkedCaseId: "8D-2041",
    problemRecordId: "PR-2026-014",
    problemRecordStatus: "Active — Validation",
  },
  {
    id: "PR-02",
    view: "products",
    name: "InverterDrive ID-8",
    description: "SiC traction inverter platform, 2025 MY onward.",
    claims: 268,
    cost: 1420000,
    ratePer1000: 21.5,
    severity: "High",
    severityScore: 8,
    trend: "Rising",
    trendPct: 11.2,
    repeatOccurrence: false,
    repeatCount: 0,
    customerImpact: 78,
    carLine: "EV-X Platform",
    component: "Traction Inverter",
    supplier: "Helios Power Systems",
    failureMode: "Thermal derate",
    problemRecordId: "PR-2026-016",
    problemRecordStatus: "Active — Root Cause",
  },
  {
    id: "PR-03",
    view: "products",
    name: "AeroPump V3",
    description: "Coolant circulation pump used on hybrid and EV thermal loops.",
    claims: 186,
    cost: 640000,
    ratePer1000: 12.9,
    severity: "High",
    severityScore: 8,
    trend: "Stable",
    trendPct: -2.1,
    repeatOccurrence: true,
    repeatCount: 1,
    customerImpact: 58,
    carLine: "Hybrid Series",
    component: "Coolant Pump",
    supplier: "Fluidyne Systems",
    failureMode: "Dynamic seal leak @ 800h",
    linkedCaseId: "8D-2039",
  },
  {
    id: "PR-04",
    view: "products",
    name: "ControlNode K2",
    description: "Domain controller running shared bootloader 4.x.",
    claims: 197,
    cost: 410000,
    ratePer1000: 14.2,
    severity: "Medium",
    severityScore: 5,
    trend: "Falling",
    trendPct: -22.6,
    repeatOccurrence: true,
    repeatCount: 3,
    customerImpact: 44,
    carLine: "ICE Legacy",
    component: "Infotainment Head Unit",
    supplier: "Lumen Electronics",
    failureMode: "Boot loop",
    linkedCaseId: "8D-2036",
    problemRecordId: "PR-2026-008",
    problemRecordStatus: "Closed — Prevention verified",
  },
  {
    id: "PR-05",
    view: "products",
    name: "DriveUnit DU-9",
    description: "Fleet-duty e-axle, high mileage accumulation profile.",
    claims: 154,
    cost: 720000,
    ratePer1000: 9.6,
    severity: "Medium",
    severityScore: 7,
    trend: "Rising",
    trendPct: 7.9,
    repeatOccurrence: false,
    repeatCount: 0,
    customerImpact: 52,
    carLine: "Fleet",
    component: "Drive Unit",
    supplier: "Rotorwerk GmbH",
    failureMode: "Bearing wear",
    linkedCaseId: "8D-2031",
  },

  // ---------- CAR LINES ----------
  {
    id: "CL-01",
    view: "carlines",
    name: "EV-X Platform",
    description: "Battery-electric flagship platform — 12,480 VIN population in service.",
    claims: 806,
    cost: 5980000,
    ratePer1000: 28.4,
    severity: "Critical",
    severityScore: 9,
    trend: "Rising",
    trendPct: 14.8,
    repeatOccurrence: true,
    repeatCount: 2,
    customerImpact: 92,
    carLine: "EV-X Platform",
    component: "HV Battery Pack",
    supplier: "Nordcell Energy AB",
    failureMode: "Thermal runaway",
    linkedCaseId: "8D-2041",
    problemRecordId: "PR-2026-014",
    problemRecordStatus: "Active — Validation",
  },
  {
    id: "CL-02",
    view: "carlines",
    name: "Hybrid Series",
    description: "Parallel-hybrid mid-size line with shared brake and thermal hardware.",
    claims: 512,
    cost: 2140000,
    ratePer1000: 19.1,
    severity: "High",
    severityScore: 8,
    trend: "Stable",
    trendPct: 1.9,
    repeatOccurrence: true,
    repeatCount: 1,
    customerImpact: 68,
    carLine: "Hybrid Series",
    component: "Brake System",
    supplier: "FrenoTech S.p.A.",
    failureMode: "Residual drag",
    problemRecordId: "PR-2026-011",
    problemRecordStatus: "Active — Effectiveness",
  },
  {
    id: "CL-03",
    view: "carlines",
    name: "Fleet",
    description: "Commercial duty-cycle variants with accelerated mileage accumulation.",
    claims: 388,
    cost: 1680000,
    ratePer1000: 24.6,
    severity: "High",
    severityScore: 7,
    trend: "Rising",
    trendPct: 9.3,
    repeatOccurrence: false,
    repeatCount: 0,
    customerImpact: 61,
    carLine: "Fleet",
    component: "Drive Unit",
    supplier: "Rotorwerk GmbH",
    failureMode: "Bearing wear",
    linkedCaseId: "8D-2031",
  },
  {
    id: "CL-04",
    view: "carlines",
    name: "ICE Legacy",
    description: "Mature combustion platform, declining population but high claim base.",
    claims: 341,
    cost: 890000,
    ratePer1000: 8.7,
    severity: "Medium",
    severityScore: 5,
    trend: "Falling",
    trendPct: -16.4,
    repeatOccurrence: true,
    repeatCount: 3,
    customerImpact: 39,
    carLine: "ICE Legacy",
    component: "Infotainment Head Unit",
    supplier: "Lumen Electronics",
    failureMode: "Boot loop",
    problemRecordId: "PR-2026-008",
    problemRecordStatus: "Closed — Prevention verified",
  },
  {
    id: "CL-05",
    view: "carlines",
    name: "EV-X Long Range",
    description: "Extended-pack derivative sharing B-22xx cell supply.",
    claims: 204,
    cost: 1310000,
    ratePer1000: 26.9,
    severity: "High",
    severityScore: 9,
    trend: "Rising",
    trendPct: 21.7,
    repeatOccurrence: true,
    repeatCount: 1,
    customerImpact: 83,
    carLine: "EV-X Long Range",
    component: "HV Battery Pack",
    supplier: "Nordcell Energy AB",
    failureMode: "Thermal runaway",
    linkedCaseId: "8D-2041",
    problemRecordId: "PR-2026-014",
    problemRecordStatus: "Active — Validation",
  },

  // ---------- SUPPLIERS ----------
  {
    id: "S-01",
    view: "suppliers",
    name: "Nordcell Energy AB",
    description: "Li-ion cell supplier — batch B-22xx separator porosity drift under containment.",
    claims: 616,
    cost: 4460000,
    ratePer1000: 31.2,
    severity: "Critical",
    severityScore: 9,
    trend: "Rising",
    trendPct: 17.5,
    repeatOccurrence: true,
    repeatCount: 2,
    customerImpact: 94,
    carLine: "EV-X Platform",
    component: "HV Battery Pack",
    supplier: "Nordcell Energy AB",
    failureMode: "Separator porosity drift",
    linkedCaseId: "8D-2041",
    problemRecordId: "PR-2026-014",
    problemRecordStatus: "Active — Validation",
  },
  {
    id: "S-02",
    view: "suppliers",
    name: "Helios Power Systems",
    description: "Power electronics supplier for SiC inverter stages.",
    claims: 291,
    cost: 1560000,
    ratePer1000: 23.4,
    severity: "High",
    severityScore: 8,
    trend: "Rising",
    trendPct: 12.7,
    repeatOccurrence: false,
    repeatCount: 0,
    customerImpact: 76,
    carLine: "EV-X Platform",
    component: "Traction Inverter",
    supplier: "Helios Power Systems",
    failureMode: "Thermal derate",
    problemRecordId: "PR-2026-016",
    problemRecordStatus: "Active — Root Cause",
  },
  {
    id: "S-03",
    view: "suppliers",
    name: "FrenoTech S.p.A.",
    description: "Brake caliper and seal supplier across hybrid programs.",
    claims: 244,
    cost: 1030000,
    ratePer1000: 18.6,
    severity: "High",
    severityScore: 8,
    trend: "Stable",
    trendPct: 0.6,
    repeatOccurrence: true,
    repeatCount: 1,
    customerImpact: 73,
    carLine: "Hybrid Series",
    component: "Brake System",
    supplier: "FrenoTech S.p.A.",
    failureMode: "Elastomer swell",
    problemRecordId: "PR-2026-011",
    problemRecordStatus: "Active — Effectiveness",
  },
  {
    id: "S-04",
    view: "suppliers",
    name: "Rotorwerk GmbH",
    description: "Bearing and rotating-hardware supplier, CpK improvement plan open.",
    claims: 168,
    cost: 790000,
    ratePer1000: 10.4,
    severity: "Medium",
    severityScore: 7,
    trend: "Rising",
    trendPct: 6.4,
    repeatOccurrence: false,
    repeatCount: 0,
    customerImpact: 50,
    carLine: "Fleet",
    component: "Drive Unit",
    supplier: "Rotorwerk GmbH",
    failureMode: "Roundness variance",
    linkedCaseId: "8D-2031",
  },
  {
    id: "S-05",
    view: "suppliers",
    name: "Lumen Electronics",
    description: "Infotainment and gateway electronics; bootloader 4.x owner.",
    claims: 212,
    cost: 448000,
    ratePer1000: 15.1,
    severity: "Medium",
    severityScore: 5,
    trend: "Falling",
    trendPct: -19.8,
    repeatOccurrence: true,
    repeatCount: 3,
    customerImpact: 42,
    carLine: "ICE Legacy",
    component: "Infotainment Head Unit",
    supplier: "Lumen Electronics",
    failureMode: "Watchdog race",
    linkedCaseId: "8D-2036",
    problemRecordId: "PR-2026-008",
    problemRecordStatus: "Closed — Prevention verified",
  },

  // ---------- FAILURE MODES ----------
  {
    id: "FM-01",
    view: "failure-modes",
    name: "Thermal runaway / separator breach",
    description: "Cell-level separator porosity drift permitting internal short and pack disable.",
    claims: 412,
    cost: 3840000,
    ratePer1000: 33.0,
    severity: "Critical",
    severityScore: 9,
    trend: "Rising",
    trendPct: 18.4,
    repeatOccurrence: true,
    repeatCount: 2,
    customerImpact: 96,
    carLine: "EV-X Platform",
    component: "HV Battery Pack",
    supplier: "Nordcell Energy AB",
    failureMode: "Thermal runaway",
    linkedCaseId: "8D-2041",
    problemRecordId: "PR-2026-014",
    problemRecordStatus: "Active — Validation",
  },
  {
    id: "FM-02",
    view: "failure-modes",
    name: "Power stage thermal derate",
    description: "Gate-driver junction temperature exceeds derate threshold under sustained load.",
    claims: 268,
    cost: 1420000,
    ratePer1000: 21.5,
    severity: "High",
    severityScore: 8,
    trend: "Rising",
    trendPct: 11.2,
    repeatOccurrence: false,
    repeatCount: 0,
    customerImpact: 78,
    carLine: "EV-X Platform",
    component: "Traction Inverter",
    supplier: "Helios Power Systems",
    failureMode: "Thermal derate",
    problemRecordId: "PR-2026-016",
    problemRecordStatus: "Active — Root Cause",
  },
  {
    id: "FM-03",
    view: "failure-modes",
    name: "Elastomer swell / residual drag",
    description: "Seal material incompatibility with fluid spec causes residual clamp load.",
    claims: 231,
    cost: 980000,
    ratePer1000: 17.8,
    severity: "High",
    severityScore: 8,
    trend: "Stable",
    trendPct: 1.4,
    repeatOccurrence: true,
    repeatCount: 1,
    customerImpact: 71,
    carLine: "Hybrid Series",
    component: "Brake System",
    supplier: "FrenoTech S.p.A.",
    failureMode: "Elastomer swell",
    problemRecordId: "PR-2026-011",
    problemRecordStatus: "Active — Effectiveness",
  },
  {
    id: "FM-04",
    view: "failure-modes",
    name: "Firmware watchdog race",
    description: "Init-sequence race in bootloader 4.x triggers reset loop on cold soak.",
    claims: 197,
    cost: 410000,
    ratePer1000: 14.2,
    severity: "Medium",
    severityScore: 5,
    trend: "Falling",
    trendPct: -22.6,
    repeatOccurrence: true,
    repeatCount: 3,
    customerImpact: 44,
    carLine: "ICE Legacy",
    component: "Infotainment Head Unit",
    supplier: "Lumen Electronics",
    failureMode: "Watchdog race",
    linkedCaseId: "8D-2036",
    problemRecordId: "PR-2026-008",
    problemRecordStatus: "Closed — Prevention verified",
  },
  {
    id: "FM-05",
    view: "failure-modes",
    name: "Bearing roundness variance",
    description: "Out-of-round race geometry accelerates wear and NVH onset.",
    claims: 154,
    cost: 720000,
    ratePer1000: 9.6,
    severity: "Medium",
    severityScore: 7,
    trend: "Rising",
    trendPct: 7.9,
    repeatOccurrence: false,
    repeatCount: 0,
    customerImpact: 52,
    carLine: "Fleet",
    component: "Drive Unit",
    supplier: "Rotorwerk GmbH",
    failureMode: "Roundness variance",
    linkedCaseId: "8D-2031",
  },
];

export type WarrantyClaim = {
  id: string;
  date: string; // ISO
  product: string;
  carLine: string;
  component: string;
  supplier: string;
  failureMode: string;
  cost: number;
  mileage: number;
  mis: number; // months in service
  severity: RiskLevel;
  complaint: string;
  repair: string;
  dealer: string;
};

// Claim evidence keyed by Top5Item id. Every view's items resolve to a
// claim set so the drill-down always has underlying evidence.
export const claimsByItem: Record<string, WarrantyClaim[]> = {
  battery: [
    {
      id: "CLM-884201",
      date: "2026-08-19",
      product: "PowerCell X7",
      carLine: "EV-X Platform",
      component: "HV Battery Pack",
      supplier: "Nordcell Energy AB",
      failureMode: "Thermal runaway precursor",
      cost: 11480,
      mileage: 18420,
      mis: 14,
      severity: "Critical",
      complaint:
        "Vehicle displayed 'Propulsion Reduced' then shut down; smell of hot electronics reported.",
      repair: "Pack replaced, cells from batch B-2204 quarantined for teardown.",
      dealer: "Metro EV — Detroit, MI",
    },
    {
      id: "CLM-883947",
      date: "2026-08-11",
      product: "PowerCell X7",
      carLine: "EV-X Platform",
      component: "HV Battery Pack",
      supplier: "Nordcell Energy AB",
      failureMode: "Cell voltage delta fault",
      cost: 9260,
      mileage: 22910,
      mis: 17,
      severity: "Critical",
      complaint: "Repeated charge interruption at 62% SOC; DTC P0AFA logged.",
      repair: "Module 6 replaced; impedance scan flagged 3 outlier cells.",
      dealer: "Lakeside Motors — Chicago, IL",
    },
    {
      id: "CLM-883410",
      date: "2026-07-29",
      product: "PowerCell X7",
      carLine: "EV-X Long Range",
      component: "HV Battery Pack",
      supplier: "Nordcell Energy AB",
      failureMode: "Thermal event — contained",
      cost: 14750,
      mileage: 31240,
      mis: 21,
      severity: "Critical",
      complaint:
        "Smoke observed at charge port during DC fast charge; vehicle isolated by customer.",
      repair: "Full pack replacement, field incident report filed to safety board.",
      dealer: "Pacific Drive — Fremont, CA",
    },
    {
      id: "CLM-882998",
      date: "2026-07-16",
      product: "PowerCell X7",
      carLine: "EV-X Platform",
      component: "HV Battery Pack",
      supplier: "Nordcell Energy AB",
      failureMode: "Isolation resistance low",
      cost: 7340,
      mileage: 12080,
      mis: 9,
      severity: "High",
      complaint: "Warning lamp for high-voltage isolation after car wash.",
      repair: "Module 2 replaced; IR scan confirmed separator anomaly.",
      dealer: "Southgate EV — Austin, TX",
    },
    {
      id: "CLM-882551",
      date: "2026-07-02",
      product: "PowerCell X7",
      carLine: "EV-X Platform",
      component: "HV Battery Pack",
      supplier: "Nordcell Energy AB",
      failureMode: "Charge derate",
      cost: 4120,
      mileage: 26740,
      mis: 19,
      severity: "High",
      complaint: "Charging speed limited to 38 kW; customer reports doubled charge times.",
      repair: "BMS reflash + module 4 swap under containment plan.",
      dealer: "Northline Auto — Toronto, ON",
    },
    {
      id: "CLM-882117",
      date: "2026-06-24",
      product: "PowerCell X7",
      carLine: "Fleet",
      component: "HV Battery Pack",
      supplier: "Nordcell Energy AB",
      failureMode: "Pack disable",
      cost: 12890,
      mileage: 48310,
      mis: 23,
      severity: "Critical",
      complaint: "Delivery van stranded mid-route with total propulsion loss.",
      repair: "Pack replaced; fleet telematics flagged 2 sibling VINs for inspection.",
      dealer: "FleetOne Service — Columbus, OH",
    },
  ],
  inverter: [
    {
      id: "CLM-881904",
      date: "2026-08-14",
      product: "InverterDrive ID-8",
      carLine: "EV-X Platform",
      component: "Traction Inverter",
      supplier: "Helios Power Systems",
      failureMode: "Thermal derate",
      cost: 3980,
      mileage: 21440,
      mis: 15,
      severity: "High",
      complaint: "Power reduced climbing grade with trailer; message 'Performance Limited'.",
      repair: "Inverter assembly replaced; coolant plate contact re-torqued.",
      dealer: "Rocky Mtn EV — Denver, CO",
    },
    {
      id: "CLM-881622",
      date: "2026-08-03",
      product: "InverterDrive ID-8",
      carLine: "EV-X Platform",
      component: "Traction Inverter",
      supplier: "Helios Power Systems",
      failureMode: "IGBT gate fault",
      cost: 4360,
      mileage: 17220,
      mis: 12,
      severity: "High",
      complaint: "Intermittent loss of drive at highway speed.",
      repair: "Power module replaced, firmware thermal map updated.",
      dealer: "Coastal Motors — Tampa, FL",
    },
    {
      id: "CLM-881310",
      date: "2026-07-21",
      product: "InverterDrive ID-8",
      carLine: "EV-X Platform",
      component: "Traction Inverter",
      supplier: "Helios Power Systems",
      failureMode: "Overtemp shutdown",
      cost: 3220,
      mileage: 9860,
      mis: 7,
      severity: "Medium",
      complaint: "Vehicle shut down after 25 minutes of track-mode driving.",
      repair: "Thermal interface material reworked per service bulletin.",
      dealer: "Summit Performance — Phoenix, AZ",
    },
    {
      id: "CLM-880977",
      date: "2026-07-08",
      product: "InverterDrive ID-8",
      carLine: "EV-X Long Range",
      component: "Traction Inverter",
      supplier: "Helios Power Systems",
      failureMode: "Thermal derate",
      cost: 4110,
      mileage: 30140,
      mis: 20,
      severity: "High",
      complaint: "Repeat derate events logged by telematics before customer complaint.",
      repair: "Inverter replaced; unit returned for supplier failure analysis.",
      dealer: "Great Lakes EV — Milwaukee, WI",
    },
  ],
  brake: [
    {
      id: "CLM-880544",
      date: "2026-08-09",
      product: "Hybrid Series",
      carLine: "Hybrid Series",
      component: "Brake System",
      supplier: "FrenoTech S.p.A.",
      failureMode: "Residual brake drag",
      cost: 1840,
      mileage: 34120,
      mis: 24,
      severity: "High",
      complaint: "Burning smell after highway drive, front rotors discolored.",
      repair: "Both front calipers replaced, seals from lot LT-77 quarantined.",
      dealer: "Valley Hybrid — Sacramento, CA",
    },
    {
      id: "CLM-880201",
      date: "2026-07-27",
      product: "Hybrid Series",
      carLine: "Hybrid Series",
      component: "Brake System",
      supplier: "FrenoTech S.p.A.",
      failureMode: "Pad wear premature",
      cost: 960,
      mileage: 19870,
      mis: 15,
      severity: "Medium",
      complaint: "Front pads worn to minimum at 19k miles.",
      repair: "Pads and caliper seal kit replaced.",
      dealer: "Eastport Auto — Boston, MA",
    },
    {
      id: "CLM-879880",
      date: "2026-07-13",
      product: "Hybrid Series",
      carLine: "Hybrid Series",
      component: "Brake System",
      supplier: "FrenoTech S.p.A.",
      failureMode: "Residual brake drag",
      cost: 2110,
      mileage: 41260,
      mis: 28,
      severity: "High",
      complaint: "Reduced fuel economy and pulling to left under coast.",
      repair: "Caliper assembly replaced, brake fluid flushed to updated spec.",
      dealer: "Prairie Motors — Omaha, NE",
    },
  ],
  infotainment: [
    {
      id: "CLM-879511",
      date: "2026-08-06",
      product: "ControlNode K2",
      carLine: "ICE Legacy",
      component: "Infotainment Head Unit",
      supplier: "Lumen Electronics",
      failureMode: "Cold-start boot loop",
      cost: 420,
      mileage: 8940,
      mis: 6,
      severity: "Medium",
      complaint: "Screen cycles logo repeatedly on cold mornings.",
      repair: "OTA 4.2.1 applied; verified with cold-soak retest.",
      dealer: "Northland Auto — Minneapolis, MN",
    },
    {
      id: "CLM-879244",
      date: "2026-07-24",
      product: "ControlNode K2",
      carLine: "ICE Legacy",
      component: "Infotainment Head Unit",
      supplier: "Lumen Electronics",
      failureMode: "Boot loop",
      cost: 1180,
      mileage: 15320,
      mis: 11,
      severity: "Medium",
      complaint: "No radio, no backup camera at startup.",
      repair: "Head unit replaced (pre-patch stock), 4.2.1 flashed.",
      dealer: "Riverbend Motors — Buffalo, NY",
    },
    {
      id: "CLM-878903",
      date: "2026-07-05",
      product: "ControlNode K2",
      carLine: "ICE Legacy",
      component: "Infotainment Head Unit",
      supplier: "Lumen Electronics",
      failureMode: "Watchdog reset",
      cost: 380,
      mileage: 22110,
      mis: 16,
      severity: "Low",
      complaint: "Occasional screen blackout while driving.",
      repair: "Software update only; no hardware replacement required.",
      dealer: "Summit Auto — Salt Lake City, UT",
    },
  ],
  driveunit: [
    {
      id: "CLM-878620",
      date: "2026-08-01",
      product: "DriveUnit DU-9",
      carLine: "Fleet",
      component: "Drive Unit",
      supplier: "Rotorwerk GmbH",
      failureMode: "Bearing wear / NVH",
      cost: 5240,
      mileage: 52880,
      mis: 26,
      severity: "Medium",
      complaint: "Growling noise from rear axle above 40 mph.",
      repair: "Drive unit replaced; bearing sent for roundness metrology.",
      dealer: "FleetOne Service — Columbus, OH",
    },
    {
      id: "CLM-878312",
      date: "2026-07-19",
      product: "DriveUnit DU-9",
      carLine: "Fleet",
      component: "Drive Unit",
      supplier: "Rotorwerk GmbH",
      failureMode: "Bearing wear",
      cost: 4890,
      mileage: 47410,
      mis: 24,
      severity: "Medium",
      complaint: "Vibration through floor under load.",
      repair: "Bearing set replaced, supplier lot RW-3391 flagged.",
      dealer: "Midland Fleet — Kansas City, MO",
    },
    {
      id: "CLM-877995",
      date: "2026-06-30",
      product: "DriveUnit DU-9",
      carLine: "Fleet",
      component: "Drive Unit",
      supplier: "Rotorwerk GmbH",
      failureMode: "NVH complaint",
      cost: 3120,
      mileage: 39560,
      mis: 21,
      severity: "Low",
      complaint: "Whine at steady cruise reported by two fleet drivers.",
      repair: "Gear set shimmed, monitored via telematics.",
      dealer: "Harbor Logistics — Newark, NJ",
    },
  ],
};

const claimBucketByComponent: Record<string, keyof typeof claimsByItem> = {
  "HV Battery Pack": "battery",
  "Traction Inverter": "inverter",
  "Brake System": "brake",
  "Infotainment Head Unit": "infotainment",
  "Drive Unit": "driveunit",
  "Coolant Pump": "brake",
};

export function claimsForComponent(component: string): WarrantyClaim[] {
  const bucket = claimBucketByComponent[component] ?? "battery";
  return claimsByItem[bucket] ?? [];
}

export function claimsForItem(item: Top5Item): WarrantyClaim[] {
  return claimsForComponent(item.component);
}

const trendRank: Record<Trend, number> = { Rising: 2, Stable: 1, Falling: 0 };

export function rankValue(item: Top5Item, basis: RankBasis): number {
  switch (basis) {
    case "claims":
      return item.claims;
    case "cost":
      return item.cost;
    case "rate":
      return item.ratePer1000 ?? 0;
    case "severity":
      return item.severityScore;
    case "trend":
      return trendRank[item.trend] * 1000 + item.trendPct;
    case "customerImpact":
      return item.customerImpact;
    case "repeat":
      return item.repeatCount * 1000 + (item.repeatOccurrence ? 1 : 0);
  }
}

export function rankedItems(view: Top5View, basis: RankBasis): Top5Item[] {
  return top5Items
    .filter((i) => i.view === view)
    .slice()
    .sort((a, b) => rankValue(b, basis) - rankValue(a, basis))
    .slice(0, 5);
}

export function formatRankValue(item: Top5Item, basis: RankBasis): string {
  switch (basis) {
    case "claims":
      return `${item.claims.toLocaleString()} claims`;
    case "cost":
      return `$${(item.cost / 1_000_000).toFixed(2)}M`;
    case "rate":
      return item.ratePer1000 != null ? `${item.ratePer1000.toFixed(1)} C/1000` : "n/a";
    case "severity":
      return `Sev ${item.severityScore}`;
    case "trend":
      return `${item.trendPct > 0 ? "+" : ""}${item.trendPct.toFixed(1)}% MoM`;
    case "customerImpact":
      return `Impact ${item.customerImpact}`;
    case "repeat":
      return item.repeatOccurrence ? `${item.repeatCount}× repeat` : "First occurrence";
  }
}

export const currency = (n: number) =>
  n >= 1_000_000 ? `$${(n / 1_000_000).toFixed(2)}M` : `$${(n / 1_000).toFixed(0)}K`;
